import React from 'react';
import CustomHead from '@/components/Common/Layout/Head';
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { Layout } from '@/components/Common';
import { MDXRemote } from 'next-mdx-remote';
import { NextPageWithLayout } from '@/pages/_app';
import { Post } from '@/shared/types/blog';
import { WEBSITE_URL } from '@/shared/constants';
import { createClient } from '@/shared/utils/prismicio';
import { convertToUTC, formatPost, isS3Image } from '@/shared/utils/utils';
import { filter } from '@prismicio/client';
import { serialize } from 'next-mdx-remote/serialize';
import { map, shuffle, sampleSize, lowerFirst } from 'lodash-es';
import { ArrowLeft } from '@/components/Common/Icons';
import BlogCard from '@/components/Blog/BlogCard';
import MakeItHappenNow from '@/components/Blog/MakeItHappenNow';
import { useRouter } from 'next/router';
import TableOfContents from '@/components/Common/TableOfContents';
import { StructuredDataAuthorType, StructuredDataType } from '@/shared/types/structured-data';
import Image from 'next/image';
import { fetchBlogData } from '../index';
import '../common.less';
import './index.less';
import { buildCloudFrontURL } from '@pietra/utils';

export async function getStaticPaths() {
  const { postList = [] } = await fetchBlogData();
  const paths = postList.map((page) => ({ params: { category: lowerFirst(page?.data?.category), uid: page?.uid } }));

  return {
    paths,
    fallback: 'blocking',
  };
}

export async function getStaticProps({ params }: GetStaticPropsContext) {
  try {
    const client = createClient();
    const res = await client.getByUID('blog', params?.uid as string);

    if (!isNaN(Number(params.uid)) && isNaN(Number(res.uid))) {
      return {
        redirect: {
          destination: res.url,
          permanent: true,
        },
      };
    }

    const post: Post = formatPost(res);
    const mdxSource = await serialize(post?.data?.content);

    const recommendedPosts = await client.get({
      filters: [filter.at('my.blog.category', post?.data?.category)],
      pageSize: 3,
    });

    const recommendBlog = sampleSize(
      shuffle(map(recommendedPosts.results, (result) => formatPost(result))).filter((blog) => blog.uid !== post.uid),
      2,
    );

    return {
      props: {
        post,
        recommendBlog,
        mdxSource,
      },
      revalidate: 3600,
    };
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
    };
  }
}

const BlogDetail: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = ({
  post,
  recommendBlog,
  mdxSource,
}) => {
  const PostImage = ({ src, alt }: { src: string; alt: string }) => (
    <Image
      src={src}
      alt={alt}
      width={800}
      height={418}
      sizes="(max-width: 831.99px) calc(100vw - 32px),(min-width: 832px) 800px, 800px"
    />
  );

  const mdxComponents = {
    img: PostImage,
  };

  const utm = `?utm_source=blog&utm_medium=${post?.uid}&utm_campaign=${post?.uid}`;
  const registerLink = `/sell-on-pietra/register${utm}`;

  const router = useRouter();

  const showSignup = () => {
    window.location.href = registerLink;
  };

  return (
    <>
      <div className="blog-post-header">
        <div className="blog-post-header-content">
          <div className="back" onClick={() => router.push('/blog')}>
            <ArrowLeft />
            <span style={{ paddingLeft: 8 }}>All posts</span>
          </div>
          <div className="blog-info">
            <span>{post?.data?.category}</span>
            <span className="dot"></span>
            {post?.data?.reading_time && (
              <>
                <span>{post?.data?.reading_time} MIN</span>
                <span className="dot"></span>
              </>
            )}
            <span>{post?.data?.created_at}</span>
          </div>
          <h1>{post?.data?.title}</h1>
          <div className="subtitle">{post?.data?.subtitle}</div>
          <div className="author-info">
            {post?.data?.author_avator?.url && (
              <Image src={post.data.author_avator.url} alt="author avator" width={36} height={36} className="avator" />
            )}
            <div className="author">
              <p>{post?.data?.author}</p>
              <p>{post?.data?.author_position}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="blog-post-container">
        <div className="post-content">
          {post?.data?.image?.url && !post.data.image.url.toLowerCase().includes('.gif') && (
            <Image
              src={post.data.image.url}
              alt="post image"
              width={800}
              height={418}
              className="post-image"
              priority
              sizes="(min-width: 832px) 800px,(max-width: 831.99px) calc(100vw - 32px), 800px"
            />
          )}
          <TableOfContents
            containerClassName=".post-content"
            offsetTop={-70}
            offsetBottom={80}
            className="blog-table-of-contents"
          />
          <MDXRemote {...mdxSource} components={mdxComponents} />
        </div>
        <div className="back" onClick={() => router.push('/blog')}>
          <ArrowLeft />
          <span style={{ paddingLeft: 8 }}>All posts</span>
        </div>
      </div>
      <MakeItHappenNow style={{ marginTop: 80, width: 1148 }} btnOnclick={showSignup} />
      <div className="dicover-more">
        <div className="read-more">
          <span>Discover more</span>
        </div>
        <div className="recomend-blog">
          {map(recommendBlog, (item) => {
            return <BlogCard key={item?.uid} post={item} />;
          })}
        </div>
      </div>
    </>
  );
};

BlogDetail.getLayout = (page: React.ReactElement) => {
  const { post }: { post: Post } = page.props;

  return (
    <>
      <CustomHead
        title={post?.data?.seo_title}
        description={post?.data?.seo_description?.substring(0, 170)}
        url={`${WEBSITE_URL}/blog/${post?.data?.category.toLowerCase()}/${post?.uid}`}
        image={
          post?.data?.image?.url && isS3Image(post?.data?.image?.url)
            ? buildCloudFrontURL(post?.data?.image?.url.replace('pietra.imgix.net', 'static.pietrastudio.com'))
            : post?.data?.image?.url
        }
        structuredDataType={StructuredDataType.NewsArticle}
        author={[
          {
            '@type': StructuredDataAuthorType.Person,
            name: post.data.author,
            url: 'https://www.pietrastudio.com',
          },
        ]}
        datePublished={convertToUTC(post.data.created_at_date)}
        dateModified={convertToUTC(post.data.created_at_date)}
      />

      <Layout>{page}</Layout>
    </>
  );
};

export default BlogDetail;
